import { sortPrompts } from '@/util/sort';
import IconButton from '@/components/common/IconButton';
import SearchField from '@/components/common/SearchField';
import { useApplication } from '@/hooks/useApplication';
import { Prompt, PromptSortType } from '@/types/prompt';
import { filterPrompts, promptSearchOptions } from '@/util/prompts';
import { isString } from '@/util/typeGuards';
import { Box, Checkbox, FormControlLabel, List, ListItem, ListItemText } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';

type AddPromptsToGroupsDialogProps = {
  open: boolean;
  promptGroupId: number;
  onClose: () => void;
};

const AddPromptsToGroupsDialog: FC<AddPromptsToGroupsDialogProps> = (props) => {
  const { application, setApplication } = useApplication();

  const [prompts, setPrompts] = useState<Prompt[]>(application.prompts);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<PromptSortType>('label');
  const [invert, setInvert] = useState<boolean>(false);

  const filteredPrompts = filterPrompts(prompts, searchQuery);

  const handleChecked = (isChecked: boolean, promptId: number) => {
    const newPath = application.promptGroups?.find((group) => group.id === props.promptGroupId)?.path || '';
    if (isChecked) {
      setPrompts(
        prompts?.map((prompt) =>
          prompt.id === promptId
            ? {
                ...prompt,
                path: newPath,
                promptGroup: props.promptGroupId
              }
            : prompt
        )
      );
    } else {
      const applicationPrompt = application.prompts.find((prompt) => prompt.id === promptId);
      applicationPrompt &&
        setPrompts(
          prompts?.map((prompt) =>
            prompt.id === promptId
              ? {
                  ...prompt,
                  path: applicationPrompt.path || '',
                  promptGroup:
                    applicationPrompt.promptGroup !== prompts.find((prompt) => prompt.id === promptId)?.promptGroup
                      ? applicationPrompt.promptGroup
                      : 0
                }
              : prompt
          )
        );
    }
  };

  const handleSave = () => {
    prompts && setApplication({ ...application, prompts: prompts });
    handleClose();
  };

  const handleClose = () => props.onClose();

  useEffect(() => {
    setPrompts(application.prompts);
  }, [props.promptGroupId, application.prompts]);

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        Add Prompts to Group -{' '}
        {application?.promptGroups?.find((promptGroup) => promptGroup.id === props.promptGroupId)?.name || 'All'}
        {prompts.length > 2 && (
          <SearchField
            searchOptions={promptSearchOptions}
            searchQuery={searchQuery}
            setSearchQuery={(query) => setSearchQuery(query)}
            sortBy={sortBy}
            invert={invert}
            onSort={(changeSortBy, reverse) => {
              changeSortBy && setSortBy(changeSortBy as PromptSortType);
              reverse && setInvert(!invert);
            }}
            autoFocus
          />
        )}
      </DialogTitle>
      <DialogContent>
        <List sx={{ width: '100%' }}>
          {sortPrompts(filteredPrompts, sortBy, invert, 'file')?.map((prompt, index) => (
            <ListItem key={`prompt.${prompt.id}.${index}`} sx={{ p: 0 }}>
              <ListItemText>
                <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        defaultChecked={prompt.promptGroup === props.promptGroupId}
                        color='default'
                        onChange={(value) => handleChecked(value.target.checked, prompt.id)}
                      />
                    }
                    label={
                      <Box>
                        <span style={{ marginRight: '8px' }}>{prompt.label || prompt.file}</span>
                        <code>
                          {prompt.path && (isString(prompt.path) ? prompt.path + '/' : `[${prompt.path?.name}]/`)}
                          {prompt.file}
                        </code>
                      </Box>
                    }
                  />
                  <span>
                    Group:{' '}
                    {application?.promptGroups?.find((group) => group.id === prompt.promptGroup)?.name || 'No Group'}
                  </span>
                </Box>
              </ListItemText>
              <IconButton onClick={() => {}}></IconButton>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color='primary'>
          Close
        </Button>
        <Button onClick={handleSave} color='primary'>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPromptsToGroupsDialog;
